import {
  Component,
  inject,
  ViewChild
} from '@angular/core';
import { Cut } from '../../../../models/cut';
import { Store } from '@ngrx/store';
import {
  deleteCut, generateCutsDXF,
  selectCut, setCutShow,
  updateCut
} from '../../../../features/cut/cut.actions';
import {
  selectCurrentCut,
} from '../../../../features/cut/cut.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { selectMeasure } from '../../../../features/measure/measure.actions';
import { selectVolume } from '../../../../features/volume/volume.actions';
import {
  selectDetection
} from '../../../../features/detection/detection.actions';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  DeleteConfirmDialog
} from '../../../dialogs/delete-confirm-dialog/delete-confirm.dialog';
import { RenameDialog } from '../../../dialogs/rename-dialog/rename.dialog';
import {
  setCameraConfiguration,
  setCameraMode,
  setMapHandleKeyboard
} from '../../../../features/cesium/cesium.actions';
import { CutChartComponent } from '../../charts/cut-chart.component';
import { CesiumTools } from '../../../../tools/cesium.tools';
import { CameraMode } from '../../../../enums/camera';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, first, Observable } from 'rxjs';
import { selectUser } from '../../../../features/user/user.selectors';
import {
  selectCurrentSite
} from '../../../../features/site/site.selectors';
import { Actions } from '@ngrx/effects';
import { Site } from '../../../../models/site';
import { User } from '../../../../models/user';
import { FormBuilder } from '@angular/forms';
import {
  selectCurrentReferenceBatch, selectCurrentReferenceBatchId,
} from '../../../../features/batch/batch.selectors';
import { DatePipe } from '@angular/common';
import {
  selectTrackerElevation, selectTrackerImage
} from '../../../../features/tracker/tracker.actions';
import {
  selectConfigDisplayCuts,
  selectConfigItemDetailExpanded
} from '../../../../features/config/config.selectors';
import {
  setConfigExpandItemDetail
} from '../../../../features/config/config.actions';


@Component({
    selector: 'cut-details',
    templateUrl: './cut-details.component.html',
    styleUrls: ['../details.component.scss', './cut-details.component.scss'],
    standalone: false
})
export class CutDetailsComponent {

  cut: Cut | null | undefined;
  user: User | null | undefined;
  slope:Array<Array<number>> = [];
  @ViewChild('graph') graph: CutChartComponent | undefined;
  private deleteDialogRef: MatDialogRef<DeleteConfirmDialog> | undefined;
  private renameDialogRef: MatDialogRef<RenameDialog> | undefined;
  private readonly _formBuilder = inject(FormBuilder);

  readonly displayCuts$ = this.store.select(selectConfigDisplayCuts);
  readonly isExpanded$ = this.store.select(selectConfigItemDetailExpanded);
  readonly currentUser$ = this.store.select(selectUser);
  readonly currentSite$ = this.store.select(selectCurrentSite);
  readonly currentBatch$ = this.store.select(selectCurrentReferenceBatch);
  readonly batchFormGroup = this._formBuilder.group({

  });

  constructor(private store: Store,
              private actions: Actions,
              private dialog: MatDialog,
              private translocoService: TranslocoService) {
    this.initHandleUser();
    this.store.select(selectCurrentCut)
      .pipe(takeUntilDestroyed())
      .subscribe((cut: Cut | null | undefined) => {
        this.cut = cut;

        if (this.cut) {
          const raw = this.cut.slope ?? [];
          this.slope = Array.isArray(raw) ? raw.map(p => [Number(p[0]), Number(p[1])]) : [];

          this.store.dispatch(selectMeasure({ id: undefined }));
          this.store.dispatch(selectVolume({ id: undefined }));
          this.store.dispatch(selectDetection({ id: undefined }));
          this.store.dispatch(selectTrackerImage({ id: undefined }));
          this.store.dispatch(selectTrackerElevation({ id: undefined }));
        } else {
          this.slope = [];
        }
      });
  }

  private initHandleUser(): void {
    this.store.select(selectUser)
      .pipe(takeUntilDestroyed())
      .subscribe((user: User | undefined) => {
        if (user) {
          this.user = user;
        }
      });
  }

  get horizontalDistance(): number | undefined {
    if (this.slope?.length) {
      const x1 = this.slope[0][0];
      const x2 = this.slope[this.slope.length-1][0];
      return Math.abs(x1 - x2);
    }
    return undefined
  }

  get verticalDistance(): number | undefined {
    if (this.slope?.length) {
      const y1 = this.slope[0][1];
      const y2 = this.slope[this.slope.length - 1][1];
      return Math.abs(y1 - y2);
    }
    return undefined;
  }

  get angle(): number | undefined {
    if (this.verticalDistance && this.horizontalDistance){
      return Math.atan2(this.verticalDistance, this.horizontalDistance) * 180.0 / Math.PI;
    }
    return undefined;
  }

  onSlopeDragging(slope: Array<Array<number>>): void {
    this.slope = slope;
  }


  onSlopeHasChanged(slope: Array<Array<number>> | undefined): void {
    this.store.select(selectCurrentSite)
      .pipe(first())
      .subscribe((site: Site | null | undefined) => {
        if (this.cut && site) {
          this.store.dispatch(updateCut({
            organizationId: site.organization,
            siteId: site.id,
            cutId: this.cut.id,
            payload: {
              slope: slope,
              name: this.cut.name,
              is_shared: this.cut.isShared
            }
          }));
        }
      });
  }

  onHandleFocus(): void {
    this.store.dispatch(setMapHandleKeyboard({mapHandleKeyboard: false}));
  }

  onClickFlyTo(cut: Cut): void {
    const cameraConfiguration = CesiumTools.cameraConfigurationFromTargetAndDirection(cut.center, cut.cameraDirection, cut.length);
    if (!cameraConfiguration) return;
    this.store.dispatch(setCameraMode({cameraMode: CameraMode.MODE_3D}));
    this.store.dispatch(setCameraConfiguration({cameraConfiguration}));
  }

  onClickClose(): void {
    this.store.dispatch(setConfigExpandItemDetail({expand: false}));
    this.store.dispatch(selectCut({id: undefined}));
  }

  onClickExpand(): void {
    this.store.dispatch(setConfigExpandItemDetail({expand: true}));
  }

  onClickCollapse(): void {
    this.store.dispatch(setConfigExpandItemDetail({expand: false}));
  }

  onClickDelete(cut: Cut): void {
    this.deleteDialogRef = this.dialog.open(DeleteConfirmDialog, {
      disableClose: false
    });
    this.deleteDialogRef.componentInstance.message = this.translocoService.translate("would_you_like_to_delete", {name: cut.name});
    this.deleteDialogRef.afterClosed().subscribe(result => {
      if(result) {
        this.store.select(selectCurrentSite)
          .pipe(first())
          .subscribe((site: Site | null | undefined) => {
            if (site) {
              this.store.dispatch(selectCut({id: undefined}));
              this.store.dispatch(deleteCut({
                organizationId: site.organization,
                siteId: site.id,
                cutId: cut.id
              }));
            }
          });
      }
    });
  }

  onClickExportGraph(cut: Cut): void {
    if (this.graph) {
      this.graph.canvasToImage(cut.name);
    }
  }

  onClickGenerateDXF(cut: Cut): void {
    combineLatest([
      this.store.select(selectCurrentSite),
      this.store.select(selectCurrentReferenceBatchId)
    ]).pipe(first())
      .subscribe(([site, referenceBatchId]) => {
        if (site && cut) {
          this.store.dispatch(generateCutsDXF({
            organizationId: site.organization,
            siteId: site.id,
            ids: [cut.id],
            batchId: site.lastBatch,
            referenceBatchId
          }));
        }
      });
  }

  onClickRename(cut: Cut): void {
    this.store.dispatch(setMapHandleKeyboard({mapHandleKeyboard: false}));
    this.renameDialogRef = this.dialog.open(RenameDialog, {
      disableClose: false
    });
    this.renameDialogRef.componentInstance.placeholder = cut.name;
    this.renameDialogRef.afterClosed().subscribe(result => {
      this.store.dispatch(setMapHandleKeyboard({mapHandleKeyboard: true}));
      if(result && this.cut) {
        this.store.select(selectCurrentSite)
          .pipe(first())
          .subscribe((site: Site | null | undefined) => {
            if (site) {
              this.store.dispatch(updateCut({
                organizationId: site.organization,
                siteId: site.id,
                cutId: cut.id,
                payload: {
                  name: result,
                  slope: cut.slope,
                  is_shared: cut.isShared
                }}));
            }
          });
      }
    });
  }

  onClickToggleShare(cut: Cut): void {
    this.store.select(selectCurrentSite)
      .pipe(first())
      .subscribe((site: Site | null | undefined) => {
        if (site) {
          this.store.dispatch(updateCut({
            organizationId: site.organization,
            siteId: site.id,
            cutId: cut.id,
            payload: {
              name: cut.name,
              slope: cut.slope,
              is_shared: !cut.isShared,
            }}));
        }
      });
  }

  onClickToggleShow(cut: Cut): void {
    this.store.dispatch(setCutShow({id: cut.id, show: !cut.show}));
  }

  onClickDeleteSlope(cut: Cut): void {
    this.slope = [];
    this.store.select(selectCurrentSite)
      .pipe(first())
      .subscribe((site: Site | null | undefined) => {
        if (site) {
          this.store.dispatch(updateCut({
            organizationId: site.organization,
            siteId: site.id,
            cutId: cut.id,
            payload: {
              name: cut.name,
              slope: null,
              is_shared: cut.isShared,
            }}));
        }
      });
  }

  formatDate(date:any | undefined): string | null {
    if(!date) return null;
    let dateFormat;
    if(this.user) {
      dateFormat = this.user.languageCode == 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy';
    }else {
      dateFormat = 'MM/dd/yyyy';
    }
    const datePipe: DatePipe = new DatePipe(this.translocoService.getActiveLang());
    return datePipe.transform(new Date(date), dateFormat);
  }

  toggleDataset(index: number) {
    if (this.graph) {
      this.graph.toggleDataset(index);
    }
  }
}


